// Helpers puros pra detectar e orquestrar a mesclagem de cadastros duplicados.
// Sem efeitos colaterais — toda escrita no banco fica no componente MemberMergeTool.

export interface MergeMember {
  id: string | number;
  full_name: string;
  email: string | null;
  whatsapp: string | null;
  country_code: string | null;
  whatsapp_e164?: string | null;
  city_state: string | null;
  profession: string | null;
  description: string | null;
  bio_long: string | null;
  photo: string | null;
  photo_position?: string | null;
  instagram: string | null;
  website: string | null;
  linkedin: string | null;
  facebook: string | null;
  tiktok: string | null;
  skills: string | null;
  languages: string | null;
  education: string | null;
  services: string | null;
  experience_years: string | null;
  has_physical_business?: boolean | null;
  business_name?: string | null;
  business_address?: string | null;
  business_city?: string | null;
  business_state?: string | null;
  business_zip?: string | null;
  created_at: string | null;
  status: string;
  merged_into_id?: string | number | null;
  merged_links?: Record<string, unknown> | null;
}

export interface LinkCounts {
  regs: number;
  banners: number;
  waitlist: number;
  testimonials: number;
}

export interface MergeField {
  key: string;
  label: string;
  icon: string;
}

export const MERGE_FIELDS: MergeField[] = [
  { key: "full_name", label: "Nome", icon: "ri-user-line" },
  { key: "email", label: "E-mail", icon: "ri-mail-line" },
  { key: "phone", label: "Telefone", icon: "ri-whatsapp-line" },
  { key: "city_state", label: "Cidade / Estado", icon: "ri-map-pin-line" },
  { key: "profession", label: "Profissão", icon: "ri-briefcase-line" },
  { key: "photo", label: "Foto", icon: "ri-camera-line" },
  { key: "photo_position", label: "Enquadramento", icon: "ri-focus-3-line" },
  { key: "description", label: "Resumo", icon: "ri-file-text-line" },
  { key: "bio_long", label: "Bio completa", icon: "ri-quill-pen-line" },
  { key: "instagram", label: "Instagram", icon: "ri-instagram-line" },
  { key: "website", label: "Website", icon: "ri-global-line" },
  { key: "linkedin", label: "LinkedIn", icon: "ri-linkedin-line" },
  { key: "facebook", label: "Facebook", icon: "ri-facebook-line" },
  { key: "tiktok", label: "TikTok", icon: "ri-tiktok-line" },
  { key: "skills", label: "Habilidades", icon: "ri-star-line" },
  { key: "languages", label: "Idiomas", icon: "ri-translate-2" },
  { key: "education", label: "Formação", icon: "ri-graduation-cap-line" },
  { key: "services", label: "Serviços", icon: "ri-service-line" },
  { key: "experience_years", label: "Experiência", icon: "ri-timer-line" },
  { key: "has_physical_business", label: "Negócio físico", icon: "ri-store-2-line" },
  { key: "business_name", label: "Nome do negócio", icon: "ri-store-2-line" },
  { key: "business_address", label: "Endereço do negócio", icon: "ri-map-pin-2-line" },
  { key: "business_city", label: "Cidade do negócio", icon: "ri-building-2-line" },
  { key: "business_state", label: "Estado do negócio", icon: "ri-flag-2-line" },
  { key: "business_zip", label: "CEP do negócio", icon: "ri-road-map-line" },
];

// Campos de lista (habilidades, idiomas, formação, serviços): o padrão é COMBINAR
// tudo em vez de escolher um lado só.
export const MERGE_LIST_FIELDS = new Set(["skills", "languages", "education", "services"]);

// Combina itens de listas separados por vírgula, ponto e vírgula ou quebra de linha:
// tira espaços sobrando, remove repetidos ignorando maiúsculas/minúsculas e acentos,
// mantém a grafia da primeira ocorrência e junta de novo com vírgula.
export function combineListValues(values: (string | null | undefined)[]): string {
  const seen = new Set<string>();
  const result: string[] = [];
  const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  values.forEach((raw) => {
    (raw || "").split(/[,;\n]/).forEach((item) => {
      const trimmed = item.trim();
      if (!trimmed) return;
      const key = norm(trimmed);
      if (!seen.has(key)) {
        seen.add(key);
        result.push(trimmed);
      }
    });
  });
  return result.join(", ");
}

// Chave de e-mail: minúsculas e sem espaços.
export function normalizeEmailKey(email?: string | null): string {
  return (email || "").toLowerCase().replace(/\s+/g, "");
}

// Chave de WhatsApp canônica (E.164): código do país + dígitos, sem espaço/pontuação.
export function normalizeWhatsappKey(whatsapp?: string | null, countryCode?: string | null): string {
  const digits = (whatsapp || "").replace(/\D/g, "");
  const cc = (countryCode || "").replace(/\D/g, "");
  return cc + digits;
}

// Valor "cru" de um campo pra comparação e escolha do melhor lado.
export function fieldValue(m: MergeMember, key: string): string {
  switch (key) {
    case "full_name": return m.full_name || "";
    case "email": return m.email || "";
    case "phone": return [m.country_code, m.whatsapp].filter(Boolean).join(" ").trim();
    case "city_state": return m.city_state || "";
    case "profession": return m.profession || "";
    case "photo": return m.photo || "";
    case "photo_position": return m.photo_position || "";
    case "description": return m.description || "";
    case "bio_long": return m.bio_long || "";
    case "instagram": return m.instagram || "";
    case "website": return m.website || "";
    case "linkedin": return m.linkedin || "";
    case "facebook": return m.facebook || "";
    case "tiktok": return m.tiktok || "";
    case "skills": return m.skills || "";
    case "languages": return m.languages || "";
    case "education": return m.education || "";
    case "services": return m.services || "";
    case "experience_years": return m.experience_years || "";
    case "has_physical_business": return m.has_physical_business ? "Sim" : "";
    case "business_name": return m.business_name || "";
    case "business_address": return m.business_address || "";
    case "business_city": return m.business_city || "";
    case "business_state": return m.business_state || "";
    case "business_zip": return m.business_zip || "";
    default: return "";
  }
}

// Escolhe o melhor valor por campo: não-vazio vence; se ambos tiverem conteúdo,
// o mais longo; em empate, o cadastro mais recente (created_at mais novo).
export function bestMemberForField(members: MergeMember[], key: string): string | number {
  let best = members[0];
  let bestLen = -1;
  let bestTime = -Infinity;

  members.forEach((m) => {
    const len = fieldValue(m, key).length;
    const time = m.created_at ? new Date(m.created_at).getTime() : -Infinity;
    if (len > bestLen || (len === bestLen && time > bestTime)) {
      bestLen = len;
      bestTime = time;
      best = m;
    }
  });

  return best.id;
}

export function defaultSelection(members: MergeMember[]): Record<string, string> {
  const selection: Record<string, string> = {};
  MERGE_FIELDS.forEach((f) => {
    if (MERGE_LIST_FIELDS.has(f.key)) {
      selection[f.key] = "combine";
    } else {
      selection[f.key] = String(bestMemberForField(members, f.key));
    }
  });
  return selection;
}

// Cadastro que "fica": o mais antigo (já referenciado em outros lugares).
// Em empate de data, o menor id.
export function pickKeptMember(members: MergeMember[]): MergeMember {
  let best = members[0];
  members.forEach((m) => {
    const t = m.created_at ? new Date(m.created_at).getTime() : Infinity;
    const bt = best.created_at ? new Date(best.created_at).getTime() : Infinity;
    if (t < bt || (t === bt && Number(m.id) < Number(best.id))) {
      best = m;
    }
  });
  return best;
}

// Cadastro "preso": marcado como merged mas sem merged_into_id (mesclagem incompleta).
export function isStuckMerged(m: MergeMember): boolean {
  return m.status === "merged" && m.merged_into_id == null;
}

// Cadastro corretamente mesclado (aponta para o sobrevivente).
export function isProperlyMerged(m: MergeMember): boolean {
  return m.status === "merged" && m.merged_into_id != null;
}

// Um grupo tem mesclagem incompleta se algum membro estiver "preso".
export function groupHasIncompleteMerge(group: MergeMember[]): boolean {
  return group.some(isStuckMerged);
}

// Agrupa duplicados por e-mail (minúsculas, sem espaço) E por WhatsApp (E.164),
// usando union-find pra agrupar também quem se conecta "em cadeia".
// Inclui "mesclagens incompletas" (merged + merged_into_id nulo) para que possam
// ser resolvidas; exclui apenas as mesclagens corretas (merged + merged_into_id).
export function detectDuplicateGroups(members: MergeMember[]): MergeMember[][] {
  const live = members.filter((m) => !isProperlyMerged(m));
  if (live.length < 2) return [];

  const parent = new Map<string, string>();
  live.forEach((m) => parent.set(String(m.id), String(m.id)));

  const find = (x: string): string => {
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root) as string;
    let cur = x;
    while (parent.get(cur) !== root) {
      const next = parent.get(cur) as string;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  };

  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  const emailIndex = new Map<string, string>();
  const whatsappIndex = new Map<string, string>();

  live.forEach((m) => {
    const ek = normalizeEmailKey(m.email);
    if (ek) {
      const existing = emailIndex.get(ek);
      if (existing) union(String(m.id), existing);
      else emailIndex.set(ek, String(m.id));
    }

    const wk = m.whatsapp_e164 || normalizeWhatsappKey(m.whatsapp, m.country_code);
    if (wk) {
      const existing = whatsappIndex.get(wk);
      if (existing) union(String(m.id), existing);
      else whatsappIndex.set(wk, String(m.id));
    }
  });

  const groups = new Map<string, MergeMember[]>();
  live.forEach((m) => {
    const root = find(String(m.id));
    const list = groups.get(root) || [];
    list.push(m);
    groups.set(root, list);
  });

  const result: MergeMember[][] = [];
  groups.forEach((list) => {
    if (list.length > 1) result.push(list);
  });

  // Ordena grupos pelo membro mais antigo primeiro (estável pra UI).
  result.sort((a, b) => {
    const aTime = Math.min(...a.map((m) => (m.created_at ? new Date(m.created_at).getTime() : Infinity)));
    const bTime = Math.min(...b.map((m) => (m.created_at ? new Date(m.created_at).getTime() : Infinity)));
    return aTime - bTime;
  });

  return result;
}

// Retrato completo do cadastro "perdedor" antes da mesclagem, com todos os campos
// originais e a data. Guardado em merged_links.snapshot para permitir recuperação.
export function snapshotMember(m: MergeMember): Record<string, unknown> {
  return {
    id: m.id,
    full_name: m.full_name,
    email: m.email,
    whatsapp: m.whatsapp,
    country_code: m.country_code,
    whatsapp_e164: m.whatsapp_e164 ?? null,
    city_state: m.city_state,
    profession: m.profession,
    description: m.description,
    bio_long: m.bio_long,
    photo: m.photo,
    photo_position: m.photo_position ?? null,
    instagram: m.instagram,
    website: m.website,
    linkedin: m.linkedin,
    facebook: m.facebook,
    tiktok: m.tiktok,
    skills: m.skills,
    languages: m.languages,
    education: m.education,
    services: m.services,
    experience_years: m.experience_years,
    has_physical_business: m.has_physical_business ?? null,
    business_name: m.business_name ?? null,
    business_address: m.business_address ?? null,
    business_city: m.business_city ?? null,
    business_state: m.business_state ?? null,
    business_zip: m.business_zip ?? null,
    status: m.status,
    created_at: m.created_at,
  };
}